import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector } from "../../Redux/Store/Store";
import { getClient } from "../../Redux/Store/Slices/client";
import Button from "../../Components/Utils/Button/Button";
import Tooltip from "../../Components/Utils/Tooltip/Tooltip";
import DocumentViewer from "../../Components/Utils/DocumentViewer/DocumentViewer";
import ConfirmAction from "../../Components/Utils/ConfirmAction/ConfirmAction";
import Popup from "../../Components/Utils/Popup/Popup";
import InviteRemoveUsers from "../../Components/Utils/InviteRemoveUsers/InviteRemoveUsers";
import { fetchLibraryFileBytes } from "../../API/Library/Library";
import {
  changeLibraryMember,
  deleteLibraryItems,
  deleteLibraryPortfolio,
  getLibraryFileDetails,
  getLibraryLinkTargets,
  resolveLibraryLinkTitles,
  getLibraryDirectory,
  getLibraryInvitableUsers,
  getLibraryPortfolio,
  inviteLibraryUser,
  LibraryFileDetails,
  LibraryFileNode,
  LibraryLinkTargets,
  LibraryPortfolioView,
  moveLibraryItems,
  patchLibraryPortfolio,
  updateLibraryFileDetails,
  uploadLibraryFiles,
} from "../../API/Library/LibraryCommands";
import { ChevronIcon, FolderIcon, SearchIcon } from "./LibraryIcons";
import FolderTree from "./FolderTree";
import FileList from "./FileList";
import FileDetailsPane from "./FileDetailsPane";
import SelectionBar from "./SelectionBar";
import FolderNameDialog, { FolderDialogState } from "./FolderNameDialog";
import MoveFolderDialog, { MoveRequest } from "./MoveFolderDialog";
import LinkEvidenceDialog from "./LinkEvidenceDialog";
import PortfolioAccessPreview from "./PortfolioAccessPreview";
import { ancestorIds, findFileFolderId, findFileTitle, findFolder, folderContains, folderHoldsFile, libraryErrorMessage, libraryInviteScopeNote, matches, patchFileInTree } from "./libraryUtils";
import { ALLOWED_UPLOAD_ACCEPT, ALLOWED_UPLOAD_LABEL, rejectedFilesToast, splitAllowedFiles } from "../../Helpers/uploadFileTypes";
import styles from "./Library.module.css";

const portfolioRoles = [
  { value: "Participant", label: "Participant" },
  { value: "Reader", label: "Reader" },
];

const globalPortfolioRoles = [{ value: "Participant", label: "Participant" }];

const LibraryPortfolioPage = () => {
  const { portfolioId = "" } = useParams();
  const client = useAppSelector(getClient);
  const navigate = useNavigate();
  const uploadRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [portfolio, setPortfolio] = useState<LibraryPortfolioView | null>(null);
  const [loadError, setLoadError] = useState("");
  const [openFolderId, setOpenFolderId] = useState("");
  const [query, setQuery] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [preview, setPreview] = useState<LibraryFileNode | null>(null);
  const [activeFileId, setActiveFileId] = useState("");
  const [details, setDetails] = useState<LibraryFileDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [links, setLinks] = useState<LibraryLinkTargets | null>(null);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOver, setDropOver] = useState("");
  const [draggingFiles, setDraggingFiles] = useState(false);
  const [folderDialog, setFolderDialog] = useState<FolderDialogState | null>(null);
  const [folderName, setFolderName] = useState("");
  const [moveRequest, setMoveRequest] = useState<MoveRequest | null>(null);
  const [confirm, setConfirm] = useState<"items" | "archive" | "delete" | null>(null);
  const previewFileId = preview?.id ?? "";
  const fetchPreviewBytes = useCallback(() => {
    if (!client?.id || !previewFileId) {
      return Promise.reject(new Error("Missing file"));
    }
    return fetchLibraryFileBytes(client.id, previewFileId);
  }, [client?.id, previewFileId]);

  const load = (next: LibraryPortfolioView) => setPortfolio(next);

  useEffect(() => {
    if (!client?.id || !portfolioId) return;
    let active = true;
    setLoadError("");
    getLibraryPortfolio(portfolioId, client.id)
      .then((data) => {
        if (active) load(data);
      })
      .catch((error) => {
        if (active) setLoadError(libraryErrorMessage(error, "This portfolio could not be loaded."));
      });
    return () => {
      active = false;
    };
  }, [client?.id, portfolioId]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (folderDialog) setFolderDialog(null);
      else if (moveRequest) setMoveRequest(null);
      else if (links) setLinks(null);
      else if (showUsers) setShowUsers(false);
      else if (activeFileId) closeDetails();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [folderDialog, moveRequest, links, showUsers, activeFileId]);

  const opened = useMemo(() => {
    if (!portfolio || !openFolderId) return null;
    return findFolder(portfolio.folders ?? [], openFolderId);
  }, [portfolio, openFolderId]);

  const currentFolders = opened?.folder.folders ?? portfolio?.folders ?? [];
  const currentFiles = opened?.folder.files ?? [];
  const crumbs = opened?.trail ?? [];
  const needle = query.trim().toLowerCase();
  const searchHits = needle && portfolio ? (portfolio.folders ?? []).flatMap((folder) => matches(folder, needle)) : [];
  const rootEmpty = !needle && (portfolio?.folders ?? []).length === 0;
  const folderEmpty = !!openFolderId && !needle && currentFolders.length === 0 && currentFiles.length === 0;
  const showList = !!portfolio && !rootEmpty && (needle || currentFolders.length > 0 || currentFiles.length > 0);
  const dropFolderName = opened?.folder.title || "";

  const run = async (work: () => Promise<LibraryPortfolioView | void>, success?: string) => {
    setBusy(true);
    try {
      const next = await work();
      if (next) load(next);
      if (success) toast.success(success);
      return next;
    } catch (error) {
      toast.error(libraryErrorMessage(error, "That action could not be completed."));
    } finally {
      setBusy(false);
    }
  };

  const openCreateFolder = () => {
    setFolderName("");
    setFolderDialog({ mode: "create" });
  };

  const openRenameFolder = (id: string, title: string) => {
    setFolderName(title);
    setFolderDialog({ mode: "rename", id, title });
  };

  const confirmFolder = () => {
    const name = folderName.trim();
    if (!client?.id || !folderDialog || !name || busy) return;
    const dialog = folderDialog;
    setFolderDialog(null);
    if (dialog.mode === "create") {
      run(() => patchLibraryPortfolio(portfolioId, client.id, {
        addFolderTitle: name,
        addFolderParentId: openFolderId,
      }), "Folder created");
      return;
    }
    if (name === dialog.title) return;
    run(() => patchLibraryPortfolio(portfolioId, client.id, {
      renameFolderId: dialog.id,
      renameFolderTitle: name,
    }), "Folder renamed");
  };

  const deleteFolder = async (id: string) => {
    if (!client?.id || !portfolio) return;
    const target = findFolder(portfolio.folders ?? [], id);
    const next = await run(() => deleteLibraryItems(portfolioId, client.id, [], [id]), "Folder deleted");
    if (!next) return;
    if (openFolderId && target && (openFolderId === id || folderContains(target.folder, openFolderId))) {
      setOpenFolderId("");
    }
    if (activeFileId && target && folderHoldsFile(target.folder, activeFileId)) {
      closeDetails();
    }
    setSelectedFolders((current) => current.filter((folderId) => folderId !== id));
  };

  const openMoveFolder = (id: string, title: string) => {
    const currentFolderId = (ancestorIds(portfolio?.folders ?? [], id) ?? []).at(-1) ?? "";
    setMoveRequest({ title, fileIds: [], folderIds: [id], currentFolderId });
  };

  const openMoveFile = (id: string, title: string) => {
    const currentFolderId = findFileFolderId(portfolio?.folders ?? [], id) ?? openFolderId;
    setMoveRequest({ title, fileIds: [id], folderIds: [], currentFolderId });
  };

  const openMoveSelection = () => {
    const folders = portfolio?.folders ?? [];
    const fileParents = selectedFiles.map((id) => findFileFolderId(folders, id) ?? openFolderId);
    const folderParents = selectedFolders.map((id) => (ancestorIds(folders, id) ?? []).at(-1) ?? "");
    const parents = [...fileParents, ...folderParents];
    const currentFolderId = parents.length > 0 && parents.every((id) => id === parents[0]) ? parents[0] : "";
    const count = selectedFiles.length + selectedFolders.length;
    const title = count === 1 && selectedFiles.length === 1
      ? findFileTitle(folders, selectedFiles[0]) || "this file"
      : count === 1
        ? findFolder(folders, selectedFolders[0])?.folder.title || "this folder"
        : `${count} items`;
    setMoveRequest({
      title,
      fileIds: selectedFiles,
      folderIds: selectedFolders,
      currentFolderId,
    });
  };

  const confirmMove = (request: MoveRequest, destinationFolderId: string) => {
    if (!client?.id) return;
    setMoveRequest(null);
    const label = request.fileIds.length > 0 && request.folderIds.length === 0 ? "File moved" : "Moved";
    run(() => moveLibraryItems(client.id, {
      fileIds: request.fileIds,
      folderIds: request.folderIds,
      destinationPortfolioId: portfolioId,
      destinationFolderId,
    }), label).then((next) => {
      if (next) {
        setSelectedFiles([]);
        setSelectedFolders([]);
      }
    });
  };

  const onDropFiles = async (fileList: FileList | null, folderId: string) => {
    setDropOver("");
    setDraggingFiles(false);
    if (!fileList?.length || !client?.id || !portfolio?.canUpload) return;
    const { allowed, rejected } = splitAllowedFiles(fileList);
    rejectedFilesToast(rejected);
    if (!allowed.length) return;
    await run(() => uploadLibraryFiles(portfolioId, client.id, folderId, allowed, false), "Files uploaded");
  };

  const moveTo = async (folderId: string, draggedFileId?: string) => {
    const fileIds = draggedFileId && !selectedFiles.includes(draggedFileId)
      ? [...selectedFiles, draggedFileId]
      : selectedFiles;
    if (!client?.id || (fileIds.length === 0 && selectedFolders.length === 0)) return;
    await run(() => moveLibraryItems(client.id, {
      fileIds,
      folderIds: selectedFolders,
      destinationPortfolioId: portfolioId,
      destinationFolderId: folderId,
    }), "Moved");
    setSelectedFiles([]);
    setSelectedFolders([]);
  };

  const closeDetails = () => {
    setActiveFileId("");
    setDetails(null);
    setDetailsLoading(false);
  };

  const openFile = async (file: LibraryFileNode) => {
    if (!client?.id) return;
    setActiveFileId(file.id);
    setDetailsLoading(true);
    try {
      const next = await getLibraryFileDetails(file.id, client.id);
      next.links = await resolveLibraryLinkTitles(portfolioId, next.file.id, client.id, next.links ?? []);
      setDetails(next);
    } catch (error) {
      toast.error(libraryErrorMessage(error, "File details could not be loaded."));
      closeDetails();
    } finally {
      setDetailsLoading(false);
    }
  };

  const applyDetails = (next: LibraryFileDetails) => {
    setDetails((current) => {
      const incoming = next.links ?? [];
      const existing = current?.links ?? [];
      return {
        ...next,
        links: incoming.map((link) => {
          if (link.questionTitle?.trim()) return link;
          const match = existing.find((item) => item.areaId === link.areaId && item.questionId === link.questionId);
          return match
            ? { ...link, questionTitle: match.questionTitle, sectionTitle: match.sectionTitle }
            : link;
        }),
      };
    });
    setPortfolio((current) => current
      ? { ...current, folders: patchFileInTree(current.folders ?? [], next.file.id, next.file) }
      : current);
  };

  const saveTags = async (tags: string[]) => {
    if (!client?.id || !details) return;
    try {
      applyDetails(await updateLibraryFileDetails(details.file.id, client.id, { tags }));
    } catch (error) {
      toast.error(libraryErrorMessage(error, "Those tags could not be saved."));
      throw error;
    }
  };

  const toggleFile = (id: string) => {
    setSelectedFiles((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const toggleFolder = (id: string) => {
    setSelectedFolders((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  return (
    <div className={styles.page}>
      <header className={styles.portfolioHeader}>
        <div className={styles.homeTitleRow}>
          <div className={styles.homeTitleGroup}>
            <button type="button" className={styles.backLink} onClick={() => navigate("/library")} aria-label="All portfolios">
              <img src="/img/left-arrow.svg" alt="" />
            </button>
            <h1>{portfolio?.title ?? "Library"}</h1>
            {portfolio && (
              <div className={styles.pills}>
                <span className={styles.pill}>{portfolio.access}</span>
                {portfolio.isLocked && (
                  <span className={`${styles.pill} ${styles.pillLock}`}>
                    <img src="/img/lock-small.svg" alt="" width={10} height={10} /> Locked
                  </span>
                )}
                {portfolio.isArchived && <span className={styles.pill}>Archived</span>}
              </div>
            )}
          </div>
          <div className={styles.actionCluster}>
          {portfolio?.canEdit && (
            <Button
              className={`${styles.headerButton} ${styles.ghostButton}`}
              colour="#fff"
              textColour="#24394F"
              border="#d5dde6"
              suppressHover
              clickHandler={openCreateFolder}
            >
              <FolderIcon size={14} /> New folder
            </Button>
          )}
          {portfolio?.canUpload && (
            <>
              <input
                ref={uploadRef}
                type="file"
                multiple
                hidden
                accept={ALLOWED_UPLOAD_ACCEPT}
                onChange={(event) => {
                  onDropFiles(event.target.files, openFolderId);
                  event.target.value = "";
                }}
              />
              <Tooltip
                text={openFolderId ? `Allowed: ${ALLOWED_UPLOAD_LABEL}` : "Open a folder to upload files"}
                align="bottom"
              >
                <span>
                  <Button
                    className={styles.headerButton}
                    disabled={!openFolderId}
                    clickHandler={() => uploadRef.current?.click()}
                  >
                    <img src="/img/document-upload.svg" alt="" width={14} /> Upload
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {(portfolio?.canEdit || portfolio?.canUpload) && <span className={styles.actionDivider} aria-hidden />}
          <div className={styles.menuWrap} ref={menuRef}>
            <Tooltip text="Portfolio settings" align="bottom" sideAlign="left">
              <button type="button" className={styles.iconButton} onClick={() => setMenuOpen((open) => !open)} aria-label="Portfolio settings">
                <img className={styles.settingsGear} src="/img/gear.svg" alt="" />
              </button>
            </Tooltip>
            {menuOpen && (
              <div className={styles.menu}>
                <button type="button" className={styles.menuItem} onClick={() => {
                  setMenuOpen(false);
                  setShowUsers(true);
                }}>
                  <img src="/img/plus-minus.svg" alt="" /> View Users
                </button>
                {portfolio?.canEdit && (
                  <button type="button" className={styles.menuItem} onClick={() => {
                    setMenuOpen(false);
                    run(
                      () => patchLibraryPortfolio(portfolioId, client!.id, { isLocked: !portfolio.isLocked }),
                      portfolio.isLocked ? "Portfolio unlocked" : "Portfolio locked"
                    );
                  }}>
                    <img src="/img/lock-small.svg" alt="" /> {portfolio.isLocked ? "Unlock" : "Lock"}
                  </button>
                )}
                {portfolio?.canDelete && portfolio.isArchived && (
                  <button type="button" className={styles.menuItem} onClick={() => {
                    setMenuOpen(false);
                    if (!client?.id) return;
                    run(
                      () => patchLibraryPortfolio(portfolioId, client.id, { isArchived: false }),
                      "Portfolio restored"
                    );
                  }}>
                    <img src="/img/archive.svg" alt="" /> Restore portfolio
                  </button>
                )}
                {portfolio?.canDelete && !portfolio.isArchived && (
                  <button type="button" className={styles.menuItem} onClick={() => {
                    setMenuOpen(false);
                    setConfirm("archive");
                  }}>
                    <img src="/img/archive.svg" alt="" /> Archive portfolio
                  </button>
                )}
                {portfolio?.canDelete && portfolio.isArchived && (
                  <button type="button" className={`${styles.menuItem} ${styles.menuDanger}`} onClick={() => {
                    setMenuOpen(false);
                    setConfirm("delete");
                  }}>
                    <img src="/img/trash-red.svg" alt="" /> Delete portfolio
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      </header>
      <div className={`${styles.browser} ${activeFileId ? styles.withPane : ""}`}>
        {busy && (
          <div className={styles.busyBar} aria-hidden>
            <div className={styles.busyBarFill} />
          </div>
        )}
        <FolderTree
          folders={portfolio?.folders ?? []}
          activeId={openFolderId}
          dropOver={dropOver}
          canRename={!!portfolio?.canEdit}
          loading={!portfolio && !loadError}
          onOpen={setOpenFolderId}
          onRename={openRenameFolder}
          onMoveFolder={openMoveFolder}
          onDeleteFolder={deleteFolder}
          onDropOver={setDropOver}
          onDropFiles={onDropFiles}
          onMove={moveTo}
        />
        <section
          className={styles.main}
          onDragOver={(event) => {
            event.preventDefault();
            if (event.dataTransfer.types.includes("Files") && openFolderId) {
              setDraggingFiles(true);
              setDropOver(openFolderId);
            }
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              setDropOver("");
              setDraggingFiles(false);
            }
          }}
          onDrop={(event) => {
            event.preventDefault();
            if (event.dataTransfer.files?.length && openFolderId) onDropFiles(event.dataTransfer.files, openFolderId);
          }}
        >
          <div className={styles.mainToolbar}>
            <div className={styles.crumbs}>
              <button type="button" onClick={() => setOpenFolderId("")}>{portfolio?.title ?? "Portfolio"}</button>
              {crumbs.map((crumb, index) => (
                <React.Fragment key={crumb.id}>
                  <ChevronIcon size={12} />
                  {index === crumbs.length - 1
                    ? <span>{crumb.title}</span>
                    : <button type="button" onClick={() => setOpenFolderId(crumb.id)}>{crumb.title}</button>}
                </React.Fragment>
              ))}
            </div>
            <label className={styles.searchWrap}>
              <SearchIcon />
              <input value={query} placeholder="Filter files, tags or uploaded by" onChange={(event) => setQuery(event.target.value)} />
            </label>
          </div>
          {loadError && <div className={styles.empty}>{loadError}</div>}
          {!portfolio && !loadError && (
            <FileList
              folders={[]}
              files={[]}
              searchHits={[]}
              searching={false}
              selectedFiles={[]}
              selectedFolders={[]}
              activeFileId=""
              canEdit={false}
              loading
              onToggleFile={() => undefined}
              onToggleFolder={() => undefined}
              onOpenFolder={() => undefined}
              onSelectFile={() => undefined}
              onPreviewFile={() => undefined}
              onRenameFolder={() => undefined}
              onMoveFolder={() => undefined}
              onDeleteFolder={() => undefined}
              onMoveFile={() => undefined}
            />
          )}
          {portfolio && rootEmpty && (
            <div className={styles.emptyState}>
              <img src="/img/Resources/empty.svg" alt="" />
              <p>Create your first folder</p>
              {portfolio.canEdit && <Button clickHandler={openCreateFolder}>New folder</Button>}
            </div>
          )}
          {portfolio && folderEmpty && (
            <div className={styles.emptyDrop}>
              <p>Drop files here or Upload</p>
              <p className={styles.allowedTypes}>Allowed: {ALLOWED_UPLOAD_LABEL}</p>
              {portfolio.canUpload && (
                <Button clickHandler={() => uploadRef.current?.click()}>Upload</Button>
              )}
            </div>
          )}
          {showList && (
            <FileList
              folders={needle ? [] : currentFolders}
              files={currentFiles}
              searchHits={searchHits}
              searching={!!needle}
              selectedFiles={selectedFiles}
              selectedFolders={selectedFolders}
              activeFileId={activeFileId}
              canEdit={!!portfolio?.canEdit}
              onToggleFile={toggleFile}
              onToggleFolder={toggleFolder}
              onOpenFolder={setOpenFolderId}
              onSelectFile={openFile}
              onPreviewFile={setPreview}
              onRenameFolder={openRenameFolder}
              onMoveFolder={openMoveFolder}
              onDeleteFolder={deleteFolder}
              onMoveFile={openMoveFile}
            />
          )}
          {draggingFiles && openFolderId && (
            <div className={styles.dropOverlay}>Drop to upload to {dropFolderName}</div>
          )}
        </section>
        {activeFileId && (
          <>
            <div className={styles.detailsBackdrop} onClick={closeDetails} />
            <FileDetailsPane
              details={details?.file.id === activeFileId ? details : null}
              loading={detailsLoading}
              canEdit={!!portfolio?.canEdit}
              onClose={closeDetails}
              onPreview={() => {
                const file = details?.file;
                if (file) setPreview(file);
              }}
              onEditLinks={async () => {
                if (!client?.id) return;
                try {
                  setLinks(await getLibraryLinkTargets(portfolioId, activeFileId, client.id));
                } catch (error) {
                  toast.error(libraryErrorMessage(error, "Link targets could not be loaded."));
                }
              }}
              onAddTag={async (tag) => {
                await saveTags([...(details?.file.tags ?? []), tag]);
              }}
              onRemoveTag={async (tag) => {
                await saveTags((details?.file.tags ?? []).filter((item) => item !== tag));
              }}
              onAddComment={async (comment) => {
                if (!client?.id || !details) return;
                try {
                  applyDetails(await updateLibraryFileDetails(details.file.id, client.id, { comment }));
                } catch (error) {
                  toast.error(libraryErrorMessage(error, "That comment could not be saved."));
                  throw error;
                }
              }}
            />
          </>
        )}
        <SelectionBar
          count={selectedFiles.length + selectedFolders.length}
          canDelete={!!portfolio?.canEdit}
          onMove={openMoveSelection}
          onDelete={() => setConfirm("items")}
          onClear={() => {
            setSelectedFiles([]);
            setSelectedFolders([]);
          }}
        />
      </div>
      {preview && client?.id && (
        <DocumentViewer
          fileName={preview.title}
          extension={preview.extension}
          fetchBytes={fetchPreviewBytes}
          onClose={() => setPreview(null)}
        />
      )}
      <MoveFolderDialog
        request={moveRequest}
        folders={portfolio?.folders ?? []}
        rootTitle={portfolio?.title ?? "Portfolio"}
        busy={busy}
        onClose={() => setMoveRequest(null)}
        onConfirm={confirmMove}
      />
      <FolderNameDialog
        dialog={folderDialog}
        name={folderName}
        busy={busy}
        onNameChange={setFolderName}
        onClose={() => setFolderDialog(null)}
        onConfirm={confirmFolder}
      />
      <Popup isVisible={showUsers} onClose={() => setShowUsers(false)}>
        <InviteRemoveUsers
          usersHeading="Invited portfolio users"
          membershipLabel="portfolio"
          rolesOverride={portfolio?.access === "Global" ? globalPortfolioRoles : portfolioRoles}
          canManageUsersOverride={!!portfolio?.canInvite}
          viewUsersExtra={portfolio?.canSetGlobal && client?.id ? (
            <PortfolioAccessPreview
              portfolioId={portfolioId}
              clientId={client.id}
              access={portfolio.access}
              ownerName={portfolio.ownerName}
              isOwner={portfolio.isOwner}
              canOpenWhenPrivate={portfolio.canOpenWhenPrivate}
              onChanged={load}
              onLeave={() => navigate("/library")}
            />
          ) : undefined}
          permissionMessage="You do not have permission to invite or remove users in this portfolio."
          inviteScopeNote={portfolio ? libraryInviteScopeNote(portfolio) : undefined}
          usersRefreshKey={portfolio?.ownerName}
          membershipRestrictedMessage={portfolio?.access === "Global" && !portfolio.canSetGlobal
            ? "Only an administrator can view, invite, or remove users while this portfolio is global."
            : undefined}
          removeUsersNote={portfolio?.access === "Global" && portfolio.canSetGlobal
            ? "Everyone in the client has reader access because this portfolio is global. Removing someone here only removes an invited role. To take away that reader access, change the portfolio to private."
            : undefined}
          loadUsers={async () => {
            if (!client?.id) return [];
            return getLibraryDirectory(portfolioId, client.id);
          }}
          loadInvitableUsers={async () => {
            if (!client?.id) return [];
            return getLibraryInvitableUsers(portfolioId, client.id);
          }}
          inviteExisting={async (user, role) => {
            if (!client?.id) return;
            await changeLibraryMember(portfolioId, client.id, { sub: user.sub, role, remove: false });
          }}
          inviteNew={async (givenName, familyName, email, role) => {
            if (!client?.id) return;
            await inviteLibraryUser(portfolioId, client.id, { givenName, familyName, email, role });
          }}
          removeUser={async (user) => {
            if (!client?.id) return;
            await changeLibraryMember(portfolioId, client.id, { sub: user.sub, role: "", remove: true });
          }}
        />
      </Popup>
      <LinkEvidenceDialog
        isVisible={!!links}
        portfolioId={portfolioId}
        clientId={client?.id ?? ""}
        links={links}
        canEdit={!!portfolio?.canEdit}
        onClose={() => setLinks(null)}
        onChange={async (saved) => {
          setLinks(saved);
          const titled = client?.id
            ? await resolveLibraryLinkTitles(portfolioId, saved.fileId, client.id, saved.links ?? [])
            : saved.links;
          setDetails((current) => current ? { ...current, links: titled } : current);
        }}
      />
      <ConfirmAction
        isVisible={confirm === "items"}
        onClose={() => setConfirm(null)}
        severity="danger"
        title="Delete selected items"
        message="These files and folders will be permanently deleted."
        confirmText="Delete"
        clickHandler={async () => {
          if (!client?.id) return;
          const next = await run(() => deleteLibraryItems(portfolioId, client.id, selectedFiles, selectedFolders), "Items deleted");
          if (!next) throw new Error("Delete failed");
          if (activeFileId && selectedFiles.includes(activeFileId)) closeDetails();
          setSelectedFiles([]);
          setSelectedFolders([]);
        }}
      />
      <ConfirmAction
        isVisible={confirm === "archive"}
        onClose={() => setConfirm(null)}
        severity="warning"
        title="Archive portfolio"
        message="This portfolio will move to Archived portfolios. It can be restored from there, or permanently deleted once it is archived."
        confirmText="Archive"
        clickHandler={async () => {
          if (!client?.id) return;
          try {
            await patchLibraryPortfolio(portfolioId, client.id, { isArchived: true });
            toast.success("Portfolio archived");
            navigate("/library?section=archived");
          } catch (error) {
            toast.error(libraryErrorMessage(error, "The portfolio could not be archived."));
            throw error;
          }
        }}
      />
      <ConfirmAction
        isVisible={confirm === "delete"}
        onClose={() => setConfirm(null)}
        severity="danger"
        title="Delete portfolio"
        message="This archived portfolio and everything in it will be permanently deleted. This cannot be undone."
        confirmText="Delete"
        clickHandler={async () => {
          if (!client?.id) return;
          try {
            await deleteLibraryPortfolio(portfolioId, client.id);
            toast.success("Portfolio deleted");
            navigate("/library?section=archived");
          } catch (error) {
            toast.error(libraryErrorMessage(error, "The portfolio could not be deleted."));
            throw error;
          }
        }}
      />
    </div>
  );
};

export default LibraryPortfolioPage;
